import { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Search as SearchIcon, Stethoscope, Hospital, FlaskConical, Pill } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function Search() {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/search", { params: { q } });
      setRes(data);
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
      <h1 className="font-display text-3xl sm:text-4xl font-bold">Search facilities</h1>
      <p className="text-slate-600 text-sm mt-1">Doctors, hospitals, labs, pharmacies — all in one place.</p>

      <Card className="mt-6 p-3 rounded-2xl flex gap-2">
        <div className="relative flex-1">
          <SearchIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="search-input" className="pl-9" placeholder="Search by name…" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && run()} />
        </div>
        <Button data-testid="search-btn" onClick={run} className="bg-teal-600 text-white">{loading ? "…" : "Search"}</Button>
      </Card>

      {res && (
        <div className="mt-8 space-y-8">
          <Section icon={Stethoscope} title="Doctors" items={res.doctors} type="doctor" />
          <Section icon={Hospital} title="Hospitals" items={res.hospitals} type="hospital" />
          <Section icon={FlaskConical} title="Labs" items={res.labs} type="lab" />
          <Section icon={Pill} title="Pharmacies" items={res.pharmacies} type="pharmacy" />
        </div>
      )}
    </div>
  );
}

function Section({ icon: Icon, title, items, type }) {
  const nav = useNavigate();
  if (!items?.length) return null;
  return (
    <div>
      <div className="flex items-center gap-2 mb-3 text-slate-700">
        <Icon className="w-4 h-4 text-teal-700" />
        <div className="font-display font-bold text-lg">{title}</div>
        <Badge variant="outline" className="ml-2">{items.length}</Badge>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
        {items.map((it) => (
          <Card key={it.id} className="p-4 rounded-xl card-lift cursor-pointer" onClick={() => nav(`/facility/${type}/${it.id}`)} data-testid={`result-${type}-${it.id}`}>
            <div className="font-semibold">{it.name}</div>
            <div className="text-xs text-slate-500 mt-1">{it.specialization || it.address}</div>
            <div className="text-xs text-teal-700 font-semibold mt-2">View details →</div>
          </Card>
        ))}
      </div>
    </div>
  );
}
